import { Component } from '@theme/component';

/**
 * Short loop clips of the technologies, and the tabs of the home section.
 *
 * Each clip plays only while its card is on screen (IntersectionObserver) and only while motion is
 * allowed: it stays on the fiber image for visitors who prefer reduced motion, while the tab is
 * hidden, or after the visitor pauses the clips with the section control. A clip in a panel that is not
 * shown is not on screen, so changing tabs also changes which clip plays.
 *
 * The home section (`sections/pc-tecnologias.liquid`) has a tab bar with one tab per technology: the bar
 * is hidden until this script runs and, without it, every panel shows one after the other. The product
 * page (`blocks/pc-tecnologia-pdp.liquid`) has no bar, so the tab code stays idle there.
 *
 * @typedef {object} Refs
 * @property {HTMLVideoElement[]} [videos] - One per card that has a clip.
 * @property {HTMLButtonElement} [toggle] - Pauses or resumes every clip.
 * @property {HTMLElement} [tablist] - The tab bar of the home section (hidden until the script runs).
 * @property {HTMLButtonElement[]} [tabs] - The tab buttons, with `data-tab` (the handle of the technology).
 * @property {HTMLElement[]} [panels] - The panels, with `data-panel` (the same handle).
 *
 * @extends {Component<Refs>}
 */
class PcTechVideos extends Component {
  /** @type {AbortController | undefined} */
  #abort;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {MediaQueryList | undefined} */
  #reducedMotion;

  /** Videos currently on screen. @type {Set<HTMLVideoElement>} */
  #visible = new Set();

  /** Set when the visitor pauses the clips; blocks automatic resumes. */
  #pausedByUser = false;

  /** Handle of the technology whose tab is open. */
  #tab = '';

  connectedCallback() {
    super.connectedCallback();

    this.#startTabs();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotion.addEventListener('change', this.#sync, { signal });
    document.addEventListener('visibilitychange', this.#sync, { signal });

    this.#observer = new IntersectionObserver(this.#onIntersect, { threshold: 0.25 });
    for (const video of this.refs.videos ?? []) this.#observer.observe(video);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abort?.abort();
    this.#abort = undefined;
    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#visible.clear();
  }

  updatedCallback() {
    super.updatedCallback();

    // A section render brings the server markup back: the bar is hidden and every panel shows again
    this.#startTabs();
  }

  /**
   * Shows the tab of the clicked button. Called by the tab buttons.
   * @param {Event} event
   */
  showTab(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-tab]') : null;
    if (button instanceof HTMLElement && button.dataset.tab) this.#showTab(button.dataset.tab);
  }

  /**
   * Arrow keys, Home and End move between the tabs. Called by the tab bar.
   * @param {KeyboardEvent} event
   */
  handleTabKeydown(event) {
    const tabs = this.refs.tabs ?? [];
    const current = tabs.findIndex((tab) => tab.dataset.tab === this.#tab);
    if (current < 0) return;

    const rtl = getComputedStyle(this).direction === 'rtl';
    /** @type {Record<string, number>} */
    const moves = { ArrowRight: rtl ? -1 : 1, ArrowLeft: rtl ? 1 : -1 };
    let next = current;

    if (event.key in moves) next = (current + moves[event.key] + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;

    event.preventDefault();
    const tab = tabs[next];
    if (tab.dataset.tab) this.#showTab(tab.dataset.tab);
    tab.focus();
  }

  /** Reveals the tab bar and shows the tab that was open, or the first one. */
  #startTabs() {
    const { tablist, tabs } = this.refs;
    if (!tablist || !tabs?.length) return;

    tablist.hidden = false;

    const known = tabs.some((tab) => tab.dataset.tab === this.#tab);
    this.#showTab(known ? this.#tab : (tabs[0].dataset.tab ?? ''));
  }

  /** @param {string} name */
  #showTab(name) {
    this.#tab = name;

    for (const tab of this.refs.tabs ?? []) {
      const selected = tab.dataset.tab === name;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    }

    for (const panel of this.refs.panels ?? []) {
      panel.hidden = panel.dataset.panel !== name;
    }
  }

  /**
   * Pauses or resumes every clip from the section control.
   */
  togglePlayback() {
    const videos = this.refs.videos ?? [];
    const anyPlaying = videos.some((video) => !video.paused);

    this.#pausedByUser = anyPlaying;
    this.#sync();
  }

  /**
   * Tracks which clips are on screen.
   * @param {IntersectionObserverEntry[]} entries
   */
  #onIntersect = (entries) => {
    for (const entry of entries) {
      const video = /** @type {HTMLVideoElement} */ (entry.target);

      if (entry.isIntersecting) this.#visible.add(video);
      else this.#visible.delete(video);
    }

    this.#sync();
  };

  /** Plays the clips on screen and pauses the rest, honoring motion, visibility and the user choice. */
  #sync = () => {
    const videos = this.refs.videos ?? [];
    const allowed = !this.#reducedMotion?.matches && !document.hidden && !this.#pausedByUser;

    for (const video of videos) {
      if (allowed && this.#visible.has(video)) {
        if (video.preload === 'none') video.preload = 'auto';

        video
          .play()
          .then(() => this.#renderVideo(video, true))
          .catch(() => this.#renderVideo(video, false));
      } else {
        video.pause();
        this.#renderVideo(video, false);
      }
    }

    this.#renderToggle();
  };

  /**
   * Fades the clip in over the fiber image while it plays.
   * @param {HTMLVideoElement} video
   * @param {boolean} playing
   */
  #renderVideo(video, playing) {
    video.toggleAttribute('data-playing', playing);
    this.#renderToggle();
  }

  /** Reflects the overall state in the control label. */
  #renderToggle() {
    const { toggle, videos } = this.refs;
    if (!toggle) return;

    const playing = (videos ?? []).some((video) => !video.paused);

    toggle.setAttribute('aria-pressed', String(!playing));
    toggle.setAttribute('aria-label', (playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay) ?? '');
  }
}

if (!customElements.get('pc-tech-videos')) {
  customElements.define('pc-tech-videos', PcTechVideos);
}
